import type { ICohortQueryModel } from "@performyard/models";
import type { CreateReviewCycleRequestInput } from "@performyard/service-contracts";
import { addDaysInTimezone } from "@performyard/utils-isomorphic";

import type { AddFormsStepData, ConfiguredForm } from "../AddFormsStep/types";

import type { CycleType } from "../../Config";
import type { AutomationType } from "../CycleSettingsStep/constants";

type SettingsData = {
	cycleName?: string;
	startDate?: Date | null;
	adminsOnly?: boolean;
	lockReviews?: boolean;
	automationType?: AutomationType;
	automationEndDate?: Date | null;
	group?: { id: string; name: string } | null;
};

const WHO_CAN_EASY_ADD_REVERSE: Record<string, "admin" | "any-employee" | "manager-chain-of-command" | "subject"> = {
	"Any Employee": "any-employee",
	Subject: "subject",
	Manager: "manager-chain-of-command",
	Admin: "admin",
};

const APPROVAL_REVERSE: Record<string, "managers-and-admins" | "admins-only" | null> = {
	"No Approval Needed": null,
	"Require Approval from Admins": "admins-only",
	"Require Approval from Admins or Managers": "managers-and-admins",
};

const resolveAbsoluteDate = (
	value: Date | number | undefined,
	startDate: Date,
	isRelative: boolean,
	companyTimezone: string
): Date | undefined => {
	if (value === undefined) return undefined;
	if (value instanceof Date) return value;
	if (isRelative && typeof value === "number")
		return addDaysInTimezone({ date: startDate, days: value, timezone: companyTimezone });
	return undefined;
};

type FormAssignmentInput = NonNullable<CreateReviewCycleRequestInput["formAssignments"]>[number];

const buildStandardAssignment = (
	form: ConfiguredForm<"standard">,
	startDate: Date,
	companyTimezone: string
): FormAssignmentInput | null => {
	const { data } = form;
	const isRelative = data.isRelativeDates;
	const authorsDueDate = resolveAbsoluteDate(data.authorsDueDate, startDate, isRelative, companyTimezone);
	if (!authorsDueDate) return null;

	// When relative, signersDueDate is days from authorsDueDate (matches the
	// "Days from Authors Due Date" label and generate-form-assignments.ts).
	const signersDueDate =
		data.formSigners.length > 0
			? resolveAbsoluteDate(data.signersDueDate, authorsDueDate, isRelative, companyTimezone)
			: undefined;

	return {
		basic: true,
		formBaseId: form.formId,
		redacted: data.redactAuthors,
		visible: data.makeVisibleWhenCompleted,
		authors: data.formAuthors.map((a) => a.id),
		authorsDueDate,
		signers: data.formSigners.length > 0 ? data.formSigners.map((s) => s.id) : undefined,
		signersDueDate,
	};
};

const build360Assignment = (form: ConfiguredForm<"360">): FormAssignmentInput | null => {
	const { data } = form;
	const whoCanEasyAdd = WHO_CAN_EASY_ADD_REVERSE[data.whoCanAddForm];
	if (!whoCanEasyAdd) return null;

	const subjectChooseAuthor: "any-employee" | "self" | undefined =
		data.whoCanAddForm === "Subject" ? (data.formAuthor === "Any Employee" ? "any-employee" : "self") : undefined;

	const requiresApproval = APPROVAL_REVERSE[data.approval] ?? null;

	const useStaticDates = data.dueDatesToggle && !data.isRelativeDates;
	const authorsDueDateRaw = data.authorsDueDate;
	const signersDueDateRaw = data.signersDueDate;

	return {
		easyAdd: true,
		formBaseId: form.formId,
		redacted: data.redactAuthors,
		visible: data.makeVisibleWhenFinished,
		whoCanEasyAdd,
		subjectEmployeeChooseAuthor: subjectChooseAuthor,
		allowComments: data.allow360RequestMessage,
		isActive: !data.makeFormInactive,
		requiresApproval,
		...(useStaticDates
			? {
					authorsDueDate: authorsDueDateRaw instanceof Date ? authorsDueDateRaw : undefined,
					signersDueDate: signersDueDateRaw instanceof Date ? signersDueDateRaw : undefined,
				}
			: {
					daysToCompleteAuthors: typeof authorsDueDateRaw === "number" ? authorsDueDateRaw : undefined,
					daysToCompleteSigners:
						data.formSigners.length > 0 && typeof signersDueDateRaw === "number"
							? signersDueDateRaw
							: undefined,
				}),
		signers: data.formSigners.length > 0 ? data.formSigners.map((s) => s.id) : undefined,
	};
};

const buildExternalAssignment = (form: ConfiguredForm<"external">): FormAssignmentInput => {
	const { data } = form;
	const hasReviewer = data.reviewerName || data.reviewerEmail || data.emailMessage;
	return {
		external: true,
		formBaseId: form.formId,
		redacted: data.redactAuthors,
		visible: data.makeVisibleWhenFinished,
		authors: hasReviewer
			? [
					{
						name: data.reviewerName,
						email: data.reviewerEmail,
						emailBody: data.emailMessage,
					},
				]
			: undefined,
	};
};

const buildFormAssignments = (
	formsData: AddFormsStepData,
	startDate: Date,
	companyTimezone: string
): FormAssignmentInput[] => {
	const assignments: FormAssignmentInput[] = [];
	for (const f of formsData.forms.standard) {
		const a = buildStandardAssignment(f, startDate, companyTimezone);
		if (a) assignments.push(a);
	}
	for (const f of formsData.forms["360"]) {
		const a = build360Assignment(f);
		if (a) assignments.push(a);
	}
	for (const f of formsData.forms.external) {
		assignments.push(buildExternalAssignment(f));
	}
	return assignments;
};

export type BuildPayloadArgs = {
	cycleType: CycleType;
	settings: SettingsData;
	formsData: AddFormsStepData;
	userSelectionId: string | null;
	autoflowUserQuery: ICohortQueryModel | null;
	companyTimezone: string;
};

export const buildCreateCyclePayload = ({
	cycleType,
	settings,
	formsData,
	userSelectionId,
	autoflowUserQuery,
	companyTimezone,
}: BuildPayloadArgs): CreateReviewCycleRequestInput | null => {
	const workflowId = formsData.selectedTemplate?.id;
	if (!workflowId || !settings.startDate || !settings.cycleName) return null;

	const formAssignments = buildFormAssignments(formsData, settings.startDate, companyTimezone);
	if (formAssignments.length === 0) return null;

	const adminsOnly = settings.lockReviews ?? false;

	if (cycleType === "automated") {
		if (!autoflowUserQuery) return null;
		const autoflowType = settings.automationType === "recurring" ? "rolling" : "one-time";
		const endDate = autoflowType === "rolling" ? (settings.automationEndDate ?? null) : null;
		return {
			type: "automated",
			name: settings.cycleName,
			workflowId,
			startDate: settings.startDate,
			formAssignments,
			adminsOnly,
			autoflow: { type: autoflowType, endDate, userQuery: autoflowUserQuery },
		};
	}

	if (!userSelectionId) return null;

	if (cycleType === "group") {
		if (!settings.group?.id) return null;
		return {
			type: "group",
			name: settings.cycleName,
			workflowId,
			startDate: settings.startDate,
			userSelectionId,
			formAssignments,
			adminsOnly,
			departmentId: settings.group.id,
		};
	}

	return {
		type: "manual",
		name: settings.cycleName,
		workflowId,
		startDate: settings.startDate,
		userSelectionId,
		formAssignments,
		adminsOnly,
	};
};
